import { AppData } from "@/app/providers/app_data.class"
import { createContext, useContext, useEffect, useState } from "react"

/*
 * Loads and caches the APP level data for the duration of the session. This includes...
 *  - API /meta/terms
 *  - API /funds and /firms
 *  - API /industry/<interval> for all known intervals - daily, monthly, quarterly, annual
 *
 * Values can be read using the getXXX() accessor functions that return the state
 *
 * TODO - explore use of StatePool, Zustand or SWR for loading the data, see https://swr.vercel.app/docs/getting-started
 */

// create the Context with the singleton AppData (automatically initiates loading)
type AppDataContextType = { appData: AppData; refreshedOn: number }
const AppDataContext = createContext<AppDataContextType>({
  appData: AppData.get(),
  refreshedOn: Date.now(),
})

// custom hook functions to easily read data from the above contexts
// each hook returns the object (which may be null) and the isLoaded flag
// this allows us to change the isLoaded logic witout affecting the client
// for now, we just check for object isNotNull()
export function useAppData(): AppData {
  const { appData } = useContext(AppDataContext)
  return appData
}

export function lastRefreshUTC(): number {
  const { refreshedOn } = useContext(AppDataContext)
  return refreshedOn
}

export function AppDataProvider({ children }) {
  const appData = AppData.get()
  const refreshedOn = Date.now()
  const PROGRESS_TIMER_MS = 100

  // include a state that is updated with a timer if the AppData is not loaded
  // this will force a refresh of the entire tree
  const [checkTime, setCheckTime] = useState<number>(Date.now())

  useEffect(() => {
    // dont do anything if we have fully loaded the data
    if (appData.isLoaded()) return () => {}
    // otherwise set a timeout to update the state
    const timer = setTimeout(() => setCheckTime(Date.now()), PROGRESS_TIMER_MS)
    return () => clearTimeout(timer)
  }, [checkTime])

  return (
    <AppDataContext.Provider value={{ appData, refreshedOn }}>
      {children}
    </AppDataContext.Provider>
  )
}
